// eslint-disable-next-line no-unused-vars
import { ApolloClient } from '@apollo/client'

import settings from '../app/components/settings'

const POSTCODE_ITEM_TYPE = 'Postcode'
const ADDRESS_ITEM_TYPE = 'Address'

/**
 * Retrieves additional address data for a particular address.
 * @param {ApolloClient<any>} apolloClient
 * The GraphQL client carrying out the request.
 * @param {string} refinementId ID of an address type search response.
 * @returns {Promise<{
 *  id: string
 *  city: string
 *  line1: string
 *  line2: string
 *  line3: string
 *  postalCode: string
 *  countryIso2: string
 *  countryName: string
 *  label: string
 * }>}
 */
const retrieveAddress = async (refinementId) => {
  const url = `${settings.badaAppointmentApiUrl}/location/retrieve-address?id=${refinementId}`
  const response = await fetch(url).then((r) =>
    r.status === 200 ? r.json() : undefined
  )

  if (!response) {
    throw new Error(`No address could be retrieved for ID '${refinementId}'!`)
  }

  return response
}

/**
 * Searches for address data matching given query.
 * @param {string} query Address search query.
 * @param {string?} refinementId ID of a non-address type search response.
 * @param {number} limit Max number of search results.
 * @param {string[]} countries Lists of countries to limit the search within.
 * @param {string} filterPostcode If the search should be limited to a specific postalcode.
 * @returns {Promise<{
 *  id: string,
 *  type: string,
 *  text: string,
 *  highlight: string,
 *  description: string
 * }[]>}
 */
const searchAddresses = async ({
  searchText,
  refinementId,
  limit,
  countries = [],
  filterPostcode,
}) => {
  if (!searchText && !refinementId) {
    return []
  }
  const url = new URL(
    `${settings.badaAppointmentApiUrl}/location/search-address`
  )
  if (searchText) {
    url.searchParams.append('searchText', searchText)
  }
  if (limit) {
    url.searchParams.append('limit', limit)
  }
  if (refinementId) {
    url.searchParams.append('refinementId', refinementId)
  }

  if (!Array.isArray(countries)) {
    url.searchParams.append('countries', countries)
  } else {
    for (let idx = 0; idx < countries.length; idx += 1) {
      url.searchParams.append('countries', countries[idx])
    }
  }
  if (filterPostcode) {
    url.searchParams.append('filterPostcode', filterPostcode)
  }
  const response = await fetch(url.toString()).then((r) =>
    r.status === 200 ? r.json() : { addresses: [] }
  )
  return response.addresses
}

const serviceProviderByEmail = async (
  selectedStore,
  selectedServiceProviderEmail
) => {
  const subsystem =
    selectedStore?.simplyBookMeSubsystem == null
      ? settings.defaultSubsystem
      : selectedStore.simplyBookMeSubsystem
  const url = `${settings.badaAppointmentApiUrl}/service-providers/email?brand=${settings.brand}&storeId=${selectedStore.id}&subsystem=${subsystem}`
  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ email: selectedServiceProviderEmail }),
  }).then((r) => (r.status === 200 ? r.json() : undefined))

  if (!response) {
    throw new Error(
      `Could not get service provider with email '${selectedServiceProviderEmail}'!`
    )
  }

  return response
}

const serviceProviderById = async (
  storeId,
  simplyBookMeSubsystem,
  selectedServiceProviderId
) => {
  const subsystem =
    simplyBookMeSubsystem == null
      ? settings.defaultSubsystem
      : simplyBookMeSubsystem
  const url = `${settings.badaAppointmentApiUrl}/service-providers/id?brand=${settings.brand}&storeId=${storeId}&subsystem=${subsystem}&id=${selectedServiceProviderId}&includeHidden=true`
  const response = await fetch(url).then((r) =>
    r.status === 200 ? r.json() : undefined
  )

  if (!response) {
    throw new Error(
      `Coulöd not get service provider with id '${selectedServiceProviderId}'!`
    )
  }

  return response
}

export const getServiceProviders = async (
  storeId,
  simplyBookMeSubsystem,
  serviceId,
  includeHidden
) => {
  const subsystem =
    simplyBookMeSubsystem == null
      ? settings.defaultSubsystem
      : simplyBookMeSubsystem
  const url = `${settings.badaAppointmentApiUrl}/service-providers/?brand=${
    settings.brand
  }&storeId=${storeId}&subsystem=${subsystem}&serviceId=${serviceId}&includeHidden=${
    includeHidden || false
  }`
  const response = await fetch(url).then((r) =>
    r.status === 200 ? r.json() : undefined
  )

  return response || []
}

export {
  retrieveAddress,
  searchAddresses,
  serviceProviderByEmail,
  serviceProviderById,
  POSTCODE_ITEM_TYPE,
  ADDRESS_ITEM_TYPE,
}
